import { Controller } from "@hotwired/stimulus"
import { celebrate } from "../lib/confetti"

// Prefetch the next feed page once the Review queue drops to this many cards.
const REFILL_THRESHOLD = 8

// The feed has two modes over the same New + Waiting threads: List (the normal
// stack) and Review (one card at a time with a big action deck + keyboard
// shortcuts). Review reuses the real thread cards — it just focuses one at a
// time via CSS — so status changes flow through the same triage controller and
// the list stays in sync underneath.
export default class extends Controller {
  static targets = [
    "listTab", "reviewTab", "listCount", "reviewCount",
    "listView", "reviewView", "deck", "counter", "peek", "allDone", "sentimentRow", "platformIcon"
  ]
  static values = { mode: { type: String, default: "list" } }

  #mode = "list"
  #reviewProcessed = 0
  #reviewTotal = 0
  #current = null
  #fetching = false
  #celebrated = false

  connect() {
    // Server seeds the mode from the user profile; apply without re-persisting.
    this.#setMode(this.modeValue === "review" ? "review" : "list", { persist: false })
  }

  showList() { this.#setMode("list") }
  showReview() { this.#setMode("review") }

  onProgressUpdated(event) {
    if (this.hasListCountTarget) this.listCountTarget.textContent = event.detail.unread
    if (this.hasReviewCountTarget) this.reviewCountTarget.textContent = event.detail.review
  }

  onTriageChanged() {
    if (this.#mode !== "review") return
    this.#reviewProcessed += 1
    this.#renderReview()
  }

  onKeydown(event) {
    if (this.#mode !== "review") return
    if (event.metaKey || event.ctrlKey || event.altKey) return
    if (/input|textarea|select/i.test(document.activeElement?.tagName || "")) return
    const triage = { d: "deckDone", w: "deckWaiting", i: "deckIgnore", s: "deckSpam", o: "deckOpen", p: "deckOpenPlatform" }
    const sentiment = { 1: "positive", 2: "neutral", 3: "negative" }
    if (triage[event.key.toLowerCase()]) {
      event.preventDefault()
      this[triage[event.key.toLowerCase()]]()
    } else if (sentiment[event.key]) {
      event.preventDefault()
      this.#setSentiment(sentiment[event.key])
    }
  }

  deckDone() { this.#act("done") }
  deckWaiting() { this.#act("waiting") }
  deckIgnore() { this.#act("ignore") }
  deckSpam() { this.#act("spam") }

  deckOpen() {
    const card = this.#currentCard()
    if (card) this.#openCurrent(card)
  }

  // No-ops on cards with no permalink (e.g. items whose platform never exposed one),
  // same as the sentiment shortcuts no-op on cards with no sentiment badge.
  deckOpenPlatform() {
    const card = this.#currentCard()
    if (!card) return
    const open = card.querySelector("[data-review-platform-open]")
    if (open) window.open(open.href, "_blank", "noopener")
  }

  #openCurrent(card) {
    const open = card.querySelector("[data-review-open]")
    if (open) window.open(open.href, "_blank", "noopener")
  }

  // Setting sentiment doesn't advance the queue — it just re-labels the card in place.
  setSentiment(event) { this.#setSentiment(event.params.sentiment) }

  #act(status) {
    const card = this.#currentCard()
    if (card) this.#triageFor(card)?.applyStatus(status)
  }

  #setSentiment(label) {
    const card = this.#currentCard()
    if (card) this.#sentimentFor(card)?.setLabel(label)
  }

  #setMode(mode, { persist = true } = {}) {
    this.#mode = mode
    this.element.classList.toggle("mode-review", mode === "review")
    this.element.classList.toggle("mode-list", mode === "list")
    this.#updateTabs()
    if (mode === "review") {
      this.#reviewProcessed = 0
      // Anchor the counter total to the same backlog the tab badge shows, not
      // just the first page that happens to be loaded.
      this.#reviewTotal = parseInt(this.reviewCountTarget?.textContent, 10) || this.#newCards().length
      this.#celebrated = false
      this.#renderReview()
      if (persist) window.scrollTo({ top: 0, behavior: "smooth" })
    }
    if (persist) this.#persistMode(mode)
  }

  // Remember the chosen mode on the user profile so it carries across sessions
  // and devices; the feed controller seeds the initial mode from it.
  #persistMode(mode) {
    fetch("/preferences", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
        Accept: "application/json"
      },
      body: JSON.stringify({ key: "feed.mode", value: mode })
    }).catch(() => { /* preference is non-critical; ignore transient failures */ })
  }

  #updateTabs() {
    const review = this.#mode === "review"
    this.#styleTab(this.listTabTarget, this.listCountTarget, !review)
    this.#styleTab(this.reviewTabTarget, this.reviewCountTarget, review)
  }

  #styleTab(tab, count, active) {
    tab.classList.toggle("text-ink", active)
    tab.classList.toggle("border-brand", active)
    tab.classList.toggle("text-ink-slate", !active)
    tab.classList.toggle("border-transparent", !active)
    tab.classList.toggle("hover:text-ink", !active)
    count.classList.toggle("bg-brand/[.08]", active)
    count.classList.toggle("text-brand-deep", active)
    count.classList.toggle("bg-well", !active)
    count.classList.toggle("text-ink-slate", !active)
  }

  #renderReview() {
    const cards = this.#newCards()
    this.#maybeRefill(cards.length)

    if (cards.length === 0) {
      this.#current = null
      this.deckTarget.classList.add("hidden")
      this.counterTarget.classList.add("hidden")
      this.peekTarget.classList.add("hidden")
      this.#toggleSentimentRow(false)
      // Hold off on "all caught up" while a refill is in flight, so a page
      // boundary mid-queue never flashes the empty state.
      const done = !this.#fetching
      this.allDoneTarget.classList.toggle("hidden", !done)
      // Celebrate only a queue the user actually cleared, once.
      if (done && !this.#celebrated && this.#reviewProcessed > 0) {
        this.#celebrated = true
        celebrate()
      }
      return
    }
    this.allDoneTarget.classList.add("hidden")
    this.deckTarget.classList.remove("hidden")
    this.counterTarget.classList.remove("hidden")
    this.#celebrated = false

    // Drop stale focus from an earlier pass, but keep a card that is mid
    // slide-out so its animation stays visible until the server removes it.
    this.element.querySelectorAll("#thread-list .is-current").forEach(c => {
      if (!c.classList.contains("thread-leaving")) c.classList.remove("is-current")
    })

    const current = cards[0]
    current.classList.add("is-current")
    this.#current = current
    // Cards whose alert has sentiment off render no sentiment badge, so the 1/2/3
    // shortcuts already no-op on them; hiding the buttons stops them advertising an
    // action that does nothing. Same for app-review cards, which never had a badge.
    this.#toggleSentimentRow(!!current.querySelector('[data-controller~="sentiment-override"]'))
    // The button has no icon of its own -- it mirrors whichever platform the current
    // card is from (Reddit, X, Bluesky, ...) out of the hidden per-card icon.
    this.platformIconTarget.innerHTML = current.querySelector("[data-platform-open-icon]")?.innerHTML || ""

    const total = Math.max(this.#reviewTotal, this.#reviewProcessed + cards.length)
    const position = Math.min(this.#reviewProcessed + 1, total)
    this.counterTarget.textContent = `${position} of ${total}`

    const next = cards[1]
    if (next) {
      const text = (next.querySelector("h3")?.textContent || next.querySelector("p")?.textContent || "").trim()
      this.peekTarget.textContent = `Next: ${text.slice(0, 90)}${text.length > 90 ? "…" : ""}`
      this.peekTarget.classList.remove("hidden")
    } else {
      this.peekTarget.classList.add("hidden")
    }
  }

  // Keep the Review queue topped up: once the buffer of loaded New cards runs
  // low, pull the next feed page in the background and append it so the next
  // card is always ready. Reuses the infinite-scroll endpoint + its sentinel.
  async #maybeRefill(remaining) {
    if (this.#fetching || remaining > REFILL_THRESHOLD) return
    const sentinel = this.element.querySelector('#thread-list [data-infinite-scroll-target="sentinel"]')
    if (!sentinel?.dataset.url) return

    this.#fetching = true
    const url = sentinel.dataset.url
    sentinel.remove()
    try {
      const response = await fetch(url, { headers: { "X-Infinite-Scroll": "1", Accept: "text/html" } })
      if (response.ok) {
        this.element.querySelector("#thread-list").insertAdjacentHTML("beforeend", await response.text())
      }
    } catch { /* network error — stop refilling, the queue drains to all-done */ }
    this.#fetching = false
    if (this.#mode === "review") this.#renderReview()
  }

  #newCards() {
    return Array.from(this.element.querySelectorAll('#thread-list [data-triage-status-value="new"]'))
      .map(el => el.closest("[data-thread-id]"))
      .filter(card => card && !card.classList.contains("thread-leaving"))
  }

  #currentCard() {
    if (this.#current && this.element.contains(this.#current) && !this.#current.classList.contains("thread-leaving")) {
      return this.#current
    }
    return this.#newCards()[0] || null
  }

  #triageFor(card) {
    const el = card.querySelector('[data-controller~="triage"]')
    return el && this.application.getControllerForElementAndIdentifier(el, "triage")
  }

  #toggleSentimentRow(visible) {
    if (this.hasSentimentRowTarget) this.sentimentRowTarget.classList.toggle("hidden", !visible)
  }

  #sentimentFor(card) {
    const el = card.querySelector('[data-controller~="sentiment-override"]')
    return el && this.application.getControllerForElementAndIdentifier(el, "sentiment-override")
  }
}
